import { sessionEnded } from '../../shared/api/sessionCoordinator';
import type { CallBootstrap, CallEnvelope } from './types';
import { rtcLog } from './debug';

type Handlers = {
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (error: Error) => void;
  onEvent?: (event: CallEnvelope) => void;
};

export class CallSignalingClient {
  private ws: WebSocket | null = null;
  private reconnectTimer: number | null = null;
  private manuallyClosed = false;
  private generation = 0;
  private refreshing = false;
  private reconnectDelayMs = 1500;
  private lastErrorNotifiedAt = 0;

  constructor(
    private bootstrap: CallBootstrap,
    private readonly handlers: Handlers,
    private readonly refreshBootstrap: () => Promise<CallBootstrap>,
  ) {}

  connect() {
    if (this.refreshing) return;
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return;
    }
    if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
      return;
    }
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.manuallyClosed = false;

    void this.reconnect();
  }

  private openSocket() {
    const url = this.buildSignalingUrl();
    url.searchParams.set('token', this.bootstrap.callToken);
    url.searchParams.set('deviceId', this.bootstrap.deviceId);
    url.searchParams.set('userId', String(this.bootstrap.userId));
    url.searchParams.set('platform', this.bootstrap.platform);
    url.searchParams.set('appId', this.bootstrap.appId);

    const ws = new WebSocket(url.toString());
    let openedAt = 0;
    this.ws = ws;
    rtcLog('info', 'signaling socket opening', {
      url: `${url.origin}${url.pathname}`,
      userId: this.bootstrap.userId,
      deviceId: this.bootstrap.deviceId,
      platform: this.bootstrap.platform,
    });

    ws.onopen = () => {
      if (this.ws !== ws || this.manuallyClosed) return;
      openedAt = Date.now();
      this.lastErrorNotifiedAt = 0;
      rtcLog('info', 'signaling socket open');
      this.handlers.onOpen?.();
    };

    ws.onclose = () => {
      if (this.ws !== ws) return;
      // Upgrade alone does not prove authentication; short failures retain backoff.
      if (openedAt && Date.now() - openedAt >= 30000) this.reconnectDelayMs = 1500;
      rtcLog('info', 'signaling socket close');
      this.ws = null;
      this.handlers.onClose?.();
      this.scheduleReconnect();
    };

    ws.onerror = () => {
      if (this.ws !== ws || this.manuallyClosed) return;
      rtcLog('warn', 'signaling socket unavailable');
      const now = Date.now();
      if (now - this.lastErrorNotifiedAt > 10000) {
        this.lastErrorNotifiedAt = now;
        this.handlers.onError?.(new Error('Call signaling websocket unavailable'));
      }
    };

    ws.onmessage = (event) => {
      if (this.ws !== ws || this.manuallyClosed) return;
      try {
        const parsed = JSON.parse(String(event.data)) as CallEnvelope;
        if (parsed.protocolVersion !== 1 || !parsed.type || !parsed.action) {
          throw new Error('Invalid call signaling v1 envelope');
        }
        rtcLog('info', 'signaling event received', {
          type: parsed.type,
          action: parsed.action,
          callId: parsed.callId,
        });
        this.handlers.onEvent?.(parsed);
      } catch (error) {
        this.handlers.onError?.(
          error instanceof Error ? error : new Error(String(error)),
        );
      }
    };
  }

  private scheduleReconnect() {
    if (this.manuallyClosed || this.reconnectTimer !== null) return;
    const delay = this.reconnectDelayMs;
    this.reconnectDelayMs = Math.min(delay * 2, 30000);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      void this.reconnect();
    }, delay);
  }

  private async reconnect() {
    const generation = this.generation;
    this.refreshing = true;
    try {
      const bootstrap = await this.refreshBootstrap();
      if (this.manuallyClosed || generation !== this.generation) return;
      this.bootstrap = bootstrap;
      this.openSocket();
    } catch (error) {
      if (this.manuallyClosed || generation !== this.generation) return;
      if (sessionEnded(error)) this.manuallyClosed = true;
      this.handlers.onError?.(error instanceof Error ? error : new Error(String(error)));
      this.scheduleReconnect();
    } finally {
      this.refreshing = false;
    }
  }

  private buildSignalingUrl(): URL {
    // BFF bootstrap is the authoritative source for the public signaling
    // endpoint in split deployments. For example, the Web App page may be
    // served from https://app.locs.club while RTC signaling is exposed from
    // wss://api.locs.club/rtc-ws, so deriving from window.location alone would
    // point the socket at the wrong host.
    const url = new URL(this.bootstrap.signalingUrl || '/rtc-ws', window.location.origin);
    // Allow bootstrap to return either an HTTP(S) origin-style URL or an
    // already websocket-specific WS(S) URL. WebSocket requires ws/wss schemes.
    // If the current page is HTTPS, force wss even when an upstream proxy made
    // BFF derive ws://api.locs.club; browsers block insecure WebSockets from
    // secure pages as mixed content.
    if (window.location.protocol === 'https:' || url.protocol === 'https:' || url.protocol === 'wss:') {
      url.protocol = 'wss:';
    } else {
      // Local development and non-TLS test environments can still use ws.
      // If older bootstrap data omits signalingUrl, the relative /rtc-ws
      // fallback resolves against the current dev-server origin.
      url.protocol = 'ws:';
    }
    return url;
  }

  send<T extends Record<string, unknown>>(event: CallEnvelope<T>) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('Call signaling socket is not connected');
    }
    rtcLog('info', 'signaling event sent', {
      type: event.type,
      action: event.action,
      callId: event.callId,
    });
    this.ws.send(JSON.stringify(event));
  }

  close() {
    this.manuallyClosed = true;
    this.generation += 1;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
    this.ws = null;
  }
}
